import type { CampaignChartPoint, CampaignMetricTotals, ChartMetric } from '@metapic-apps/shared'
import type { Ref } from 'vue'

import {
  normalizeCampaignChart,
  normalizeCampaignMetricTotals,
} from '@/lib/adapters/campaign-performance.adapter'
import { useApi } from '@/lib/helpers/api.helpers'
import { socialMediaClient } from '@/lib/services/social-media.service'

/** Social-media metrics surfaced in the campaign performance chart. */
export const SOCIAL_MEDIA_CAMPAIGN_METRICS = [
  'views',
  'unique_views',
  'media_value',
  'content_produced',
] as const

type DateRange = { startDate: string; endDate: string }

const { chart, summary } = socialMediaClient

/**
 * Eagerly loads the totals for the social-media metrics from `/performance/summary`
 * so the metric buttons can show a number without selecting them first.
 */
export const useSocialMediaCampaignMetricTotals = (
  advertiserId: Ref<number | undefined>,
  campaignId: number,
  dateRange: Ref<DateRange>,
) => {
  const { data, isLoading } = useApi<CampaignMetricTotals>(async () => {
    const currentAdvertiserId = advertiserId.value
    const { startDate, endDate } = dateRange.value
    if (!currentAdvertiserId) {
      return {}
    }
    const response = await summary({
      advertiser_id: currentAdvertiserId,
      campaign_id: campaignId,
      date_from: startDate,
      date_to: endDate,
      traffic_source: 'all',
      metrics: [...SOCIAL_MEDIA_CAMPAIGN_METRICS],
    })
    return normalizeCampaignMetricTotals(response.metrics)
  })

  return { totals: data, isLoading }
}

/**
 * Lazily loads the daily chart series for the active social-media metric from
 * `/performance/chart`. When no social metric is selected no request is made.
 */
export const useSocialMediaCampaignChart = (
  advertiserId: Ref<number | undefined>,
  campaignId: number,
  metric: Ref<ChartMetric | null>,
  dateRange: Ref<DateRange>,
) => {
  const { data, isLoading } = useApi<CampaignChartPoint[]>(async () => {
    const currentAdvertiserId = advertiserId.value
    const activeMetric = metric.value
    const { startDate, endDate } = dateRange.value
    if (!currentAdvertiserId || !activeMetric) {
      return []
    }
    const response = await chart({
      advertiser_id: currentAdvertiserId,
      campaign_id: campaignId,
      date_from: startDate,
      date_to: endDate,
      traffic_source: 'all',
      metric: activeMetric,
      breakdown_by: 'traffic_source',
    })
    return normalizeCampaignChart(response)
  })

  return { points: data, isLoading }
}
